import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { CssBaseline } from "@material-ui/core";
import { styled } from '@mui/material/styles';
import "./Dashboard.css";
import Header from "./Header";
import {
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";


const AntTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#1890ff',
  },
});

const AntTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.85)',
    fontSize: '1rem',
    fontWeight: 'bold',
    // fontFamily: [
    //   '-apple-system',
    //   'BlinkMacSystemFont',
    //   '"Segoe UI"',
    //   'Roboto',
    //   '"Helvetica Neue"',
    //   'Arial',
    //   'sans-serif',
    //   '"Apple Color Emoji"',
    //   '"Segoe UI Emoji"',
    //   '"Segoe UI Symbol"',
    // ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    '&.Mui-selected': {
      color: '#1890ff',
      fontWeight: theme.typography.fontWeightMedium,
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#d1eaff',
    },
  }),
);


interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#fff',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
}));

const ExpandableCell = ({ files }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="outlined"
        size="small"
      >
        {isExpanded ? "Hide" : `Show (${files.length})`}
      </Button>
      {isExpanded && (
        <ul style={{ paddingLeft: "20px", marginTop: "5px" }}>
          {files.map((file, index) => (
            <li key={index}>{file}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Dashboard = ({ classes, username, fetchAPI }) => {
  const [data, setData] = useState([]);
  const [ahriData, setAhriData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingContext, setLoadingContext] = useState(null);

  // Function to update status using contextID
  const ahriupdateStatus = async (contextID) => {
    setLoadingContext(contextID);
    try {
      // Simulate API call to fetch new status
      const newStatus = await fetchStatusFromAPI(contextID);

      // Update the status in state
      setAhriData(prevData =>
        prevData.map(item =>
          item.contextID === contextID ? { ...item, status: newStatus } : item
        )
      );
    } catch (error) {
      console.error("Error fetching status:", error);
    }
    setLoadingContext(null);
  };

  const fetchStatusFromAPI = async (contextID, retries = 3, delay = 1000) => {
    try {
      let prefix = { contextID: contextID };
      const response = await fetchAPI.post("/ahriuploadstatus", prefix, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });

      console.log("Raw Response:", response.data);

      if (response.data && response.data.status) {
        return response.data.status;
      } else {
        throw new Error("Invalid API response: Missing 'status' field");
      }
    } catch (error) {
      if (error.response && error.response.status === 504 && retries > 0) {
        console.warn(`504 Error: Retrying in ${delay / 1000} seconds...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchStatusFromAPI(contextID, retries - 1, delay * 2); // Exponential backoff
      }

      console.error("Final error after retries:", error);
      return "Error"; // Return error status after all retries fail
    }
  };


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    fetchData();
    fetchAHRIData();
  }, []);


  useEffect(() => {
    console.log("modified", ahriData)
  }, [ahriData]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI.get("/uploads", {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else {
        console.error("Data returned is not an array:", response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };


  const fetchAHRIData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI.get("/ahriupload", {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });
  
      const data = response.data?.data || [];
      setAhriData(data);
      console.log("Fetched Data:", data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  

  const columns = React.useMemo(
    () => [
      {
        Header: "Failed Files",
        accessor: "failed_files",
        Cell: ({ cell: { value } }) => <ExpandableCell files={value || []} />,
      },
      {
        Header: "Successful Files",
        accessor: "successful_files",
        Cell: ({ cell: { value } }) => <ExpandableCell files={value || []} />,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span
            style={{
              color:
                value === "SUCCESS"
                  ? "green"
                  : value === "FAILED"
                    ? "red"
                    : "orange",
              fontWeight: "bold",
            }}
          >
            {value || "N/A"}
          </span>
        ),
      },
      {
        Header: "Uploaded By",
        accessor: "uploaded_by",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
      {
        Header: "Uploaded On",
        accessor: "uploaded_on",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    usePagination
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <Card className={classes.card}>
        <Header
          position="static"
          className={classes.appbar}
          username={username}
        />

        <CardContent>
          {/* Tabs Section */}
          <AntTabs
            value={selectedTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
          >
            <AntTab label="UPLOAD HISTORY" />
            {/* <AntTab label="AHRI UPLOAD HISTORY" /> */}
          </AntTabs>
          {selectedTab === 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography variant="h6" className={classes.btn}>
                  Upload History
                </Typography>
                <IconButton
                  onClick={fetchData}
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </div>
              <table {...getTableProps()} className="table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps()}
                          style={{ backgroundColor: "navy", color: "white" }}
                        >
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="pagination">
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                  Previous
                </button>
                <span>
                  Page{" "}
                  <strong>
                    {pageIndex + 1} of {pageOptions.length}
                  </strong>
                </span>
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                  Next
                </button>
              </div>
              <p style={{ color: "red", font: "small-caption" }}>
                * User can see upload history of last 14 days
              </p>
            </div>
          )}
          {selectedTab === 1 && (
            <div style={{ padding: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography variant="h6">AHRIUPLOAD HISTORY</Typography>
                <IconButton
                  onClick={fetchAHRIData}
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </div>
              <TableContainer component={Paper} style={{ marginTop: "1rem" }}>
                {isLoading ? (
                  <div style={{ textAlign: "center", padding: "2rem" }}>
                    <CircularProgress />
                    <p>Loading data, please wait...</p>
                  </div>
                ) : ahriData.length === 0 ? (
                  <p style={{ textAlign: "center" }}>No data available.</p>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow style={{ backgroundColor: "navy" }}>
                        <TableCell style={{ color: "white" }}>File Name</TableCell>
                        <TableCell style={{ color: "white" }}>Uploaded At</TableCell>
                        <TableCell style={{ color: "white" }}>Status</TableCell>
                        <TableCell style={{ color: "white" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ahriData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.fileName}</TableCell>
                          <TableCell>{row.uploaded_at}</TableCell>
                          <TableCell
                            style={{
                              color:
                                row.upload_status === -1
                                  ? "red"
                                  : row.upload_status === 1
                                    ? "green"
                                    : "gray",
                              fontWeight: "bold",
                            }}
                          >
                            {row.upload_status === -1
                              ? "UPLOAD FAILED"
                              : row.upload_status === 1
                                ? "SUCCESS"
                                : row.status}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => ahriupdateStatus(row.contextID)}
                              disabled={
                                row.upload_status === 0 ||
                                loadingContext === row.contextID ||
                                row.status === "Process completed."
                              }
                            >
                              {loadingContext === row.contextID ? (
                                <CircularProgress size={20} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>

            </div>
          )}

        </CardContent>
      </Card>
    </React.Fragment>
  );
};

export default Dashboard;
